import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { FormModal } from "@/components/shared/FormModal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Plus, ArrowRightLeft, UserPlus, Users, Handshake, CircleAlert, MoreVertical, Pause, Play, Archive, ArchiveRestore, UserCog, Trash2, Settings2, BarChart3, Loader2 } from "lucide-react";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();
const HEALTH = { high: { label: "Needs attention", cls: "border-red-200 text-red-700 bg-red-50" }, medium: { label: "Watch", cls: "border-amber-200 text-amber-700 bg-amber-50" }, low: { label: "Healthy", cls: "border-emerald-200 text-emerald-700 bg-emerald-50" } };
const STATUS = { suspended: { label: "Suspended", cls: "border-amber-200 text-amber-700 bg-amber-50" }, archived: { label: "Archived", cls: "border-slate-200 text-slate-600 bg-slate-100" } };

export default function Companies() {
  const { gotoTenant, refreshTenants, isPortfolioAdmin, ctx } = useAtlas();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [manageCo, setManageCo] = useState(null);
  const canManage = ctx?.is_super;

  const load = () => api.get("/companies").then((r) => setData(r.data)).catch((e) => { setData({ companies: [], totals: {} }); if (e?.response?.status !== 403) toast.error(apiError(e)); });
  useEffect(() => { load(); }, []);

  const create = async (v) => {
    const { data: res } = await api.post("/companies", { name: v.name, accent: v.accent || null, logo: v.logo || null, owner_email: v.owner_email || null, owner_name: v.owner_name || null });
    toast.success(`Company "${v.name}" created`);
    if (res?.invite?.invite_link) toast.message("Admin invite ready", { description: res.invite.invite_link });
    await refreshTenants();
    load();
  };

  const switchIn = (c) => { if (c.status !== "active") { toast.error("Resume this company before switching in"); return; } toast.success(`Switching into ${c.name}`); gotoTenant(c.slug || c.id); };

  const setStatus = async (c, status) => { try { await api.post(`/companies/${c.id}/status`, { status }); toast.success(`${c.name} ${status}`); await refreshTenants(); load(); } catch (e) { toast.error(apiError(e)); } };

  const transfer = async (c) => {
    if (!c.admin) { toast.error("Invite an admin for this company first"); return; }
    if (!window.confirm(`Transfer ownership of "${c.name}" to ${c.admin.name} (${c.admin.email})?\n\nThey become the owner and the company leaves your portfolio.`)) return;
    try { await api.post(`/companies/${c.id}/transfer`, { email: c.admin.email }); toast.success(`Ownership transferred to ${c.admin.email}`); await refreshTenants(); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (c) => {
    const name = window.prompt(`This permanently deletes "${c.name}" and ALL its data. This cannot be undone.\n\nType the company name to confirm:`);
    if (name == null) return;
    try { await api.delete(`/companies/${c.id}`, { data: { confirm: name } }); toast.success(`"${c.name}" deleted`); await refreshTenants(); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const inviteAdmin = async (c) => {
    const email = window.prompt(`Invite an admin to run "${c.name}" — their email:`);
    if (!email) return;
    const name = window.prompt("Their name (optional):") || "";
    try { const { data: res } = await api.post(`/companies/${c.id}/invite-admin`, { email, name }); toast.success("Admin invited"); if (res?.invite_link) toast.message("Activation link", { description: res.invite_link }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!isPortfolioAdmin && !(data?.companies?.length) && data && !canManage) {
    return <div data-testid="companies-page"><EmptyState icon={Building2} title="Companies" description="Independent company workspaces appear here." /></div>;
  }

  const t = data?.totals || {};
  return (
    <div data-testid="companies-page">
      <PageHeader title="Companies" subtitle="Independent workspaces — fully isolated, switchable only by you">
        {data?.companies?.length > 0 && <Button variant="outline" onClick={() => navigate("/companies/report")} data-testid="companies-report-button"><BarChart3 className="w-4 h-4 mr-1" /> Reporting</Button>}
        {canManage && <Button onClick={() => setOpen(true)} data-testid="company-create-button"><Plus className="w-4 h-4 mr-1" /> New Company</Button>}
      </PageHeader>

      {data?.companies?.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="companies-totals">
          <Totic icon={Building2} label="Companies" value={t.companies || 0} />
          <Totic icon={Users} label="Clients" value={t.clients || 0} />
          <Totic icon={Handshake} label="Open pipeline" value={money(t.pipeline_value)} />
          <Totic icon={CircleAlert} label="Overdue work" value={t.overdue || 0} tone={t.overdue ? "warn" : null} />
        </div>
      )}

      {data && data.companies.length === 0 ? (
        <EmptyState icon={Building2} title="No companies yet" description="Create a company to spin up a fresh, independent workspace with its own clients, team, pipeline and data." actionLabel={canManage ? "New Company" : undefined} onAction={() => setOpen(true)} />
      ) : (
        <div className="space-y-2.5">
          {(data?.companies || []).map((c) => {
            const h = HEALTH[c.health] || HEALTH.low;
            const st = STATUS[c.status];
            const inactive = c.status !== "active";
            return (
              <div key={c.id} data-testid={`company-row-${c.id}`} className={cn("panel p-4 flex items-center gap-4 flex-wrap hover:shadow-sm transition-shadow", inactive && "opacity-70")}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-heading font-semibold flex-shrink-0" style={{ background: c.accent || "#4f46e5" }}>
                  {c.logo ? <img src={c.logo} alt="" className="w-full h-full object-cover rounded-xl" /> : (c.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-semibold text-slate-900 truncate flex items-center gap-2">{c.name}{st ? <span className={cn("chip", st.cls)} data-testid={`company-status-${c.id}`}>{st.label}</span> : <span className={cn("chip", h.cls)}>{h.label}</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{c.admin ? <>Admin: {c.admin.name} · {c.admin.email}{c.admin.status === "invited" && " (invited)"}</> : "No admin assigned yet"}</div>
                </div>
                <div className="flex items-center gap-5 text-sm">
                  <Metric label="Clients" value={c.clients} />
                  <Metric label="Pipeline" value={money(c.pipeline_value)} />
                  <Metric label="Open" value={c.open_work} />
                  <Metric label="Overdue" value={c.overdue} tone={c.overdue ? "warn" : null} />
                </div>
                <div className="flex items-center gap-2">
                  {canManage && <Button variant="outline" size="sm" className="rounded-full" onClick={() => inviteAdmin(c)} data-testid={`company-invite-${c.id}`}><UserPlus className="w-3.5 h-3.5 mr-1" /> Invite admin</Button>}
                  <Button size="sm" className="rounded-full" onClick={() => switchIn(c)} disabled={inactive} data-testid={`company-switch-in-${c.id}`}><ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Switch in</Button>
                  {canManage && (
                    <DropdownMenu><DropdownMenuTrigger asChild><button className="p-1.5 rounded-lg hover:bg-black/5 text-slate-500" data-testid={`company-menu-${c.id}`}><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => setManageCo(c)} data-testid={`company-manage-${c.id}`}><Settings2 className="w-4 h-4 mr-2" /> Apps &amp; settings</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {c.status === "suspended"
                          ? <DropdownMenuItem onClick={() => setStatus(c, "active")} data-testid={`company-resume-${c.id}`}><Play className="w-4 h-4 mr-2" /> Resume</DropdownMenuItem>
                          : c.status !== "archived" && <DropdownMenuItem onClick={() => setStatus(c, "suspended")} data-testid={`company-suspend-${c.id}`}><Pause className="w-4 h-4 mr-2" /> Suspend</DropdownMenuItem>}
                        {c.status === "archived"
                          ? <DropdownMenuItem onClick={() => setStatus(c, "active")} data-testid={`company-restore-${c.id}`}><ArchiveRestore className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
                          : <DropdownMenuItem onClick={() => setStatus(c, "archived")} data-testid={`company-archive-${c.id}`}><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => transfer(c)} disabled={!c.admin} data-testid={`company-transfer-${c.id}`}><UserCog className="w-4 h-4 mr-2" /> Transfer ownership</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => del(c)} className="text-red-600 focus:text-red-700" data-testid={`company-delete-${c.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete company</DropdownMenuItem>
                      </DropdownMenuContent></DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FormModal open={open} onOpenChange={setOpen} title="New Company" testid="company-form-modal" onSubmit={create}
        submitLabel="Create company"
        fields={[
          { name: "name", label: "Company name", required: true, placeholder: "e.g. Northstar Detailing" },
          { name: "accent", label: "Accent colour", type: "color", default: "#4f46e5" },
          { name: "logo", label: "Logo URL (optional)", placeholder: "https://…" },
          { name: "owner_email", label: "Owner / admin email (optional)", placeholder: "admin@company.com" },
          { name: "owner_name", label: "Owner / admin name (optional)" },
        ]}
      />
      <CompanyManageDialog company={manageCo} onClose={() => setManageCo(null)} onSaved={() => { load(); refreshTenants(); }} />
    </div>
  );
}

function CompanyManageDialog({ company, onClose, onSaved }) {
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState({ name: "", accent: "#4f46e5", logo: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!company) return;
    setForm({ name: company.name || "", accent: company.accent || "#4f46e5", logo: company.logo || "", login_slug: company.login_slug || "", custom_domain: company.custom_domain || "" });
    api.get(`/companies/${company.id}/apps`).then((r) => setApps(r.data)).catch(() => setApps([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);
  if (!company) return null;
  const toggle = async (key, enabled) => {
    setApps((s) => s.map((a) => a.key === key ? { ...a, enabled } : a));
    try { await api.post(`/companies/${company.id}/apps`, { app_key: key, enabled }); }
    catch (e) { toast.error(apiError(e)); setApps((s) => s.map((a) => a.key === key ? { ...a, enabled: !enabled } : a)); }
  };
  const saveSettings = async () => {
    setBusy(true);
    try { await api.patch(`/companies/${company.id}`, { name: form.name, accent: form.accent, logo: form.logo || null, login_slug: form.login_slug || null, custom_domain: form.custom_domain || null }); toast.success("Settings saved"); onSaved?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="company-manage-dialog">
        <DialogHeader><DialogTitle className="font-heading">Manage {company.name}</DialogTitle><DialogDescription>Turn apps on or off and edit branding for this company.</DialogDescription></DialogHeader>
        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-3">
            <p className="eyebrow">Settings</p>
            <div className="space-y-1"><Label className="text-xs">Company name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-settings-name" /></div>
            <div className="flex gap-4">
              <div className="space-y-1"><Label className="text-xs">Accent</Label><div className="flex items-center gap-2"><input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-9 w-14 rounded border cursor-pointer" data-testid="company-settings-accent" /><span className="text-xs font-mono text-slate-500">{form.accent}</span></div></div>
              <div className="space-y-1 flex-1"><Label className="text-xs">Logo URL</Label><Input value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="https://…" data-testid="company-settings-logo" /></div>
            </div>
            <Button size="sm" onClick={saveSettings} disabled={busy} data-testid="company-settings-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Save settings</Button>
            <div className="space-y-1 pt-1">
              <Label className="text-xs">Branded sign-in address</Label>
              <div className="flex items-center gap-1"><span className="text-xs text-slate-400 font-mono">/c/</span><Input value={form.login_slug} onChange={(e) => setForm({ ...form, login_slug: e.target.value })} placeholder="my-company" data-testid="company-settings-slug" /></div>
              <button type="button" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/c/${form.login_slug}`); toast.success("Sign-in link copied"); }} className="text-[11px] text-blue-600 hover:underline" data-testid="company-copy-login">Copy sign-in link</button>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Custom domain (optional)</Label>
              <Input value={form.custom_domain} onChange={(e) => setForm({ ...form, custom_domain: e.target.value })} placeholder="app.theircompany.com" data-testid="company-settings-domain" />
              <p className="text-[11px] text-slate-400">Point this domain at the app and sign-in will resolve to this company's brand.</p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="eyebrow">Apps</p>
            {apps.map((a) => (
              <div key={a.key} className="flex items-center justify-between gap-3 panel p-3">
                <div className="min-w-0"><div className="text-sm font-medium text-slate-800">{a.name} <span className="text-[10px] text-slate-400">· {a.category}</span></div><div className="text-xs text-slate-500 truncate">{a.description}</div></div>
                <Switch checked={a.enabled} onCheckedChange={(v) => toggle(a.key, v)} data-testid={`company-app-${a.key}`} />
              </div>
            ))}
            {apps.length === 0 && <p className="text-xs text-slate-400">Loading apps…</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Totic = ({ icon: Icon, label, value, tone }) => (
  <div className="panel p-4">
    <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className={cn("font-heading text-2xl font-semibold tabular", tone === "warn" && "text-amber-600")}>{value}</div>
  </div>
);

const Metric = ({ label, value, tone }) => (
  <div className="text-center hidden sm:block">
    <div className={cn("font-mono tabular font-semibold text-slate-800", tone === "warn" && "text-amber-600")}>{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);
